import { Check, X } from 'lucide-react'
import PropTypes from 'prop-types'

import { CheckboxGroup } from '@/components/CheckboxGroup'
import { FormField, FormItem, FormLabel, FormControl } from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { RadioGroup } from '@/components/RadioGroup'

import DragAndDropQuestion from './QuizResultDragAndDrop'

function CorrectnessIndicator({ title, correctAnswers, responses }) {
  if (correctAnswers.includes(title)) {
    return (
      <span className="text-success ml-2 font-bold">
        <Check />
      </span>
    )
  }
  if (responses.includes(title)) {
    return (
      <span className="text-destructive ml-2 font-bold">
        <X />
      </span>
    )
  }
}
CorrectnessIndicator.propTypes = {
  title: PropTypes.string.isRequired,
  correctAnswers: PropTypes.array.isRequired,
  responses: PropTypes.array.isRequired,
}

function SingleChoiceQuestion({ question, field }) {
  const options = question.answers.map((option) => ({
    id: option.title,
    label: (
      <>
        {option.title}{' '}
        <CorrectnessIndicator
          title={option.title}
          correctAnswers={question.correctAnswers}
          responses={question.responses}
        />
      </>
    ),
  }))
  return <RadioGroup className="self-start" options={options} {...field} />
}
SingleChoiceQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
}

function TrueFalseQuestion({ field, question }) {
  const options = question.answers.map((option) => ({
    id: option.title,
    label: (
      <>
        {option.title}{' '}
        <CorrectnessIndicator
          title={option.title}
          correctAnswers={question.correctAnswers}
          responses={question.responses}
        />
      </>
    ),
  }))
  return <RadioGroup className="self-start" options={options} {...field} />
}
TrueFalseQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
}

function MultipleChoiceQuestion({ question, field }) {
  const options = question.answers.map((option) => ({
    id: option.title,
    label: (
      <>
        {option.title}{' '}
        <CorrectnessIndicator
          title={option.title}
          correctAnswers={question.correctAnswers}
          responses={question.responses}
        />
      </>
    ),
  }))
  return <CheckboxGroup disabled id={field.name} options={options} {...field} />
}
MultipleChoiceQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
}

function NumericQuestion({ question, field }) {
  return (
    <div>
      <Input type="number" {...field} className="w-96 max-w-full" />
      {/* correctAnswers is empty on a failed attempt, where the API withholds
          the answer key. Don't render a dangling "Correct answer:" label. */}
      {!question.isCorrect && question.correctAnswers.length > 0 && (
        <div className="text-muted-foreground mt-2 text-sm">
          Correct answer: {question.correctAnswers.join(' or ')}
        </div>
      )}
    </div>
  )
}
NumericQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
}

function questionInput({ question, field }) {
  switch (question.type) {
    case 'Single Choice':
      return <SingleChoiceQuestion question={question} field={field} />
    case 'Multiple Choice':
      return <MultipleChoiceQuestion question={question} field={field} />
    case 'True/False':
      return <TrueFalseQuestion field={field} question={question} />
    case 'Numeric':
      return <NumericQuestion field={field} question={question} />
    case 'Drag and Drop':
      return <DragAndDropQuestion question={question} field={field} />
    default:
      return <p>Unknown question type: {question.type}</p>
  }
}

function QuizResultQuestion({ question, control, index }) {
  return (
    <FormField
      control={control}
      // disabled={isPending}
      name={question.id}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-base">
            <strong>{index + 1}.</strong>
            {question.isCorrect !== undefined && (
              <span
                className={`font-bold ${question.isCorrect ? 'text-success' : 'text-destructive'}`}
              >
                {question.isCorrect ? <Check /> : <X />}
              </span>
            )}
            {question.text} {question.type === 'Drag and Drop' && 'Drag and drop'}
          </FormLabel>
          <FormControl>
            <div className={question.signedImagePath ? 'grid grid-cols-2 gap-4' : ''}>
              {questionInput({ question, field })}
              {question.signedImagePath && (
                <img
                  src={question.signedImagePath}
                  alt="Question related"
                  className="max-h-96 max-w-full rounded border"
                />
              )}
            </div>
          </FormControl>
        </FormItem>
      )}
    />
  )
}

export default QuizResultQuestion

QuizResultQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
}
